/**
 * My Courses - LMS
 * View assigned courses and track progress. Supports SCORM launch.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../api/axiosClient';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

function MyCourses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [launchingCourseId, setLaunchingCourseId] = useState(null);

  useEffect(() => {
    fetchMyCourses();
  }, []);

  const fetchMyCourses = async () => {
    setLoading(true);
    try {
      const response = await axiosClient.get('/courses/my-courses');
      setCourses(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError('Failed to load your courses');
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleContinueCourse = (courseId) => {
    navigate(`/courses/${courseId}/view`);
  };

  const handleLaunchScorm = async (courseId) => {
    setLaunchingCourseId(courseId);
    try {
      const response = await axiosClient.get(`/scorm/launch/course/${courseId}`);
      const launchData = response.data;
      // Open SCORM content in a new tab via the static file path
      const launchUrl = `/static/${launchData.file_path}/content/${launchData.default_launch_path}`;
      window.open(launchUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error launching SCORM course:', err);
      alert('Failed to launch SCORM course. Please contact your administrator.');
    } finally {
      setLaunchingCourseId(null);
    }
  };

  const handleDownloadCertificate = async (certificateId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/certificates/${certificateId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to download certificate');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificate-${certificateId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading certificate:', err);
      alert('Failed to download certificate');
    }
  };

  // ── Styles ─────────────────────────────────────────────────────────────────

  const containerStyle = { padding: '24px', maxWidth: '1200px', margin: '0 auto' };
  const titleStyle = { fontSize: '2rem', fontWeight: '700', color: '#1a1a1a', margin: 0 };
  const courseCardStyle = {
    background: '#fff', border: '2px solid #d4af37',
    borderRadius: '8px', padding: '24px', marginBottom: '20px',
  };
  const progressBarContainerStyle = {
    background: '#f0f0f0', borderRadius: '20px', height: '24px',
    marginBottom: '16px', overflow: 'hidden', position: 'relative',
  };
  const buttonStyle = {
    padding: '10px 20px', fontSize: '0.95rem', fontWeight: '600',
    background: '#d4af37', color: '#1a1a1a', border: 'none',
    borderRadius: '4px', cursor: 'pointer', marginRight: '8px',
  };
  const secondaryButtonStyle = {
    ...buttonStyle, background: '#fff', border: '2px solid #d4af37',
  };
  const scormButtonStyle = {
    ...buttonStyle, background: '#1565c0', color: '#fff',
  };
  const statusBadge = (isCompleted) => ({
    display: 'inline-block', padding: '6px 12px', borderRadius: '20px',
    fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase',
    background: isCompleted ? '#4caf50' : '#2196f3', color: '#fff',
    marginBottom: '12px',
  });
  const emptyStateStyle = {
    padding: '48px 24px', textAlign: 'center', background: '#fff',
    border: '2px solid #d4af37', borderRadius: '8px', color: '#666',
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <h1 style={titleStyle}>My Courses</h1>
        <div style={{ ...emptyStateStyle, marginTop: '32px' }}>Loading courses...</div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={titleStyle}>My Courses</h1>
        <p style={{ color: '#666', margin: '8px 0 0 0', fontSize: '1.1rem' }}>
          Track your learning progress and access course materials
        </p>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', background: '#ffebee', border: '2px solid #e74c3c', borderRadius: '4px', color: '#c62828', marginBottom: '24px' }}>
          {error}
        </div>
      )}

      {courses.length === 0 ? (
        <div style={emptyStateStyle}>
          <p style={{ fontSize: '1.1rem', fontWeight: '600' }}>No courses assigned yet</p>
          <p style={{ fontSize: '0.9rem', margin: '8px 0 0 0' }}>
            Contact your administrator to get course assignments
          </p>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '16px', color: '#666', fontSize: '0.95rem' }}>
            {courses.length} course{courses.length !== 1 ? 's' : ''} assigned
          </div>

          {courses.map((course) => {
            const percentage = course.progress_percentage || 0;
            const isCompleted = course.is_completed || false;
            const isScorm = course.is_scorm || false;
            const isLaunching = launchingCourseId === course.course_id;

            return (
              <div key={course.assignment_id} style={courseCardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                  <span style={statusBadge(isCompleted)}>
                    {isCompleted ? 'Completed' : 'In Progress'}
                  </span>
                  {isScorm && (
                    <span style={{
                      background: '#e3f2fd', color: '#1565c0',
                      fontSize: '0.7rem', fontWeight: '700',
                      padding: '3px 10px', borderRadius: '12px',
                      textTransform: 'uppercase', letterSpacing: '0.04em',
                    }}>
                      SCORM
                    </span>
                  )}
                </div>

                <h2 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#1a1a1a', margin: '0 0 8px 0' }}>
                  {course.course_title}
                </h2>

                {course.course_description && (
                  <p style={{ color: '#666', marginBottom: '16px', fontSize: '0.95rem' }}>
                    {course.course_description}
                  </p>
                )}

                <div style={{ marginBottom: '8px', fontSize: '0.9rem', color: '#666' }}>
                  <strong>Certificate Type:</strong>{' '}
                  <span style={{ textTransform: 'uppercase', fontWeight: '600', color: '#d4af37' }}>
                    {course.certificate_type}
                  </span>
                </div>

                <div style={{ marginBottom: '16px', fontSize: '0.9rem', color: '#666' }}>
                  <strong>Assigned:</strong>{' '}
                  {new Date(course.assigned_at).toLocaleDateString()} by {course.assigned_by}
                </div>

                {!isScorm && (
                  <div style={progressBarContainerStyle}>
                    <div style={{
                      background: 'linear-gradient(90deg, #d4af37 0%, #f4d03f 100%)',
                      height: '100%', width: `${percentage}%`, transition: 'width 0.3s ease',
                    }} />
                    <div style={{
                      position: 'absolute', width: '100%', textAlign: 'center',
                      lineHeight: '24px', fontSize: '0.85rem', fontWeight: '700', color: '#1a1a1a', top: 0,
                    }}>
                      {percentage.toFixed(0)}% Complete
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {course.is_active && isScorm && (
                    <button
                      onClick={() => handleLaunchScorm(course.course_id)}
                      disabled={isLaunching}
                      style={{ ...scormButtonStyle, opacity: isLaunching ? 0.6 : 1 }}
                    >
                      {isLaunching ? 'Launching...' : '▶ Launch Course'}
                    </button>
                  )}

                  {course.is_active && !isScorm && (
                    <button onClick={() => handleContinueCourse(course.course_id)} style={buttonStyle}>
                      {isCompleted ? 'Review Course' : 'Continue Course'}
                    </button>
                  )}

                  {isCompleted && course.certificate_id && (
                    <button onClick={() => handleDownloadCertificate(course.certificate_id)} style={secondaryButtonStyle}>
                      Download Certificate
                    </button>
                  )}

                  {!course.is_active && (
                    <div style={{ padding: '10px', color: '#999', fontStyle: 'italic' }}>
                      This course is no longer active
                    </div>
                  )}
                </div>

                {!isScorm && course.total_modules > 0 && (
                  <div style={{ marginTop: '12px', fontSize: '0.85rem', color: '#666' }}>
                    {course.completed_modules_count || 0} of {course.total_modules} modules completed
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

export default MyCourses;
